import { Button, ButtonColor, ButtonGroup, ButtonGroupSize, Container, ContainerSize, Gap, Heading, Stack, Text } from '@rebootui';
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";
type ButtonGroupProps = {}

const ButtonGroupPage = ({ }: ButtonGroupProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Button group
            </Heading>

            <Text el="p"><Text el="strong">Button groups</Text> allow multiple buttons to be displayed together as a
                single, cohesive unit. They are useful for related actions or toolbar-like layouts.</Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Example
            </Heading>
            <Text className="mb-4">
                Wrap a series of <Text el="strong">buttons</Text> in <Text el="strong">ButtonGroup</Text>.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <ButtonGroup>
                        <Button color={ButtonColor.Primary}>Primary</Button>
                        <Button color={ButtonColor.Primary}>Secondary</Button>
                        <Button color={ButtonColor.Primary}>Success</Button>
                    </ButtonGroup>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ButtonBasicCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Mixed Style group
            </Heading>
            <CodeSnippet>
                <CodePreview>
                    <ButtonGroup>
                        <Button color={ButtonColor.Danger}>Danger</Button>
                        <Button color={ButtonColor.Warning}>Warning</Button>
                        <Button color={ButtonColor.Success}>Success</Button>
                    </ButtonGroup>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ButtonMixedCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Outlined Style group
            </Heading>
            <CodeSnippet>
                <CodePreview>
                    <ButtonGroup>
                        <Button color={ButtonColor.OutlinePrimary}>Left</Button>
                        <Button color={ButtonColor.OutlinePrimary}>Middle</Button>
                        <Button color={ButtonColor.OutlinePrimary}>Right</Button>
                    </ButtonGroup>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ButtonOutlinedCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Sizing
            </Heading>
            <Text className="mb-4">
                <Text el="strong">Button groups</Text> can have different sizes to fit the layout and importance of the
                action set.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <ButtonGroup size={ButtonGroupSize.Lg}>
                        <Button color={ButtonColor.OutlineDark}>Left</Button>
                        <Button color={ButtonColor.OutlineDark}>Middle</Button>
                        <Button color={ButtonColor.OutlineDark}>Right</Button>
                    </ButtonGroup>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ButtonLGSizeCode}
                </CodeHighlighter>
            </CodeSnippet>

            <CodeSnippet>
                <CodePreview>
                    <ButtonGroup size={ButtonGroupSize.Sm}>
                        <Button color={ButtonColor.OutlineDark}>Left</Button>
                        <Button color={ButtonColor.OutlineDark}>Middle</Button>
                        <Button color={ButtonColor.OutlineDark}>Right</Button>
                    </ButtonGroup>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ButtonSMSizeCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Button Toolbar
            </Heading>
            <Text className="mb-4">
                <Text el="strong">Button toolbars</Text> allow you to combine multiple button groups into a single
                horizontal layout. This is useful for more complex UI components where multiple related actions need to
                be grouped and aligned.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <ButtonGroup>
                            <Button color={ButtonColor.Primary}>1</Button>
                            <Button color={ButtonColor.Primary}>2</Button>
                            <Button color={ButtonColor.Primary}>3</Button>
                        </ButtonGroup>
                        <ButtonGroup>
                            <Button color={ButtonColor.Success}>4</Button>
                            <Button color={ButtonColor.Success}>5</Button>
                        </ButtonGroup>
                        <ButtonGroup>
                            <Button color={ButtonColor.Danger}>6</Button>
                        </ButtonGroup>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ButtonToolbarCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Vertical Variations
            </Heading>
            <Text className="mb-4">
                <Text el="strong">Button groups</Text> can be arranged <Text el="strong">vertically</Text> instead
                of <Text el="strong">horizontally</Text>. This is useful when space is limited or when you want to
                create a stacked set of related actions.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <ButtonGroup vertical={true}>
                        <Button color={ButtonColor.Primary}>1</Button>
                        <Button color={ButtonColor.Primary}>2</Button>
                        <Button color={ButtonColor.Primary}>3</Button>
                    </ButtonGroup>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ButtonVerticalCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    )
}

export default ButtonGroupPage;

const ButtonBasicCode = `<ButtonGroup>
    <Button color={ButtonColor.Primary}>Primary</Button>
    <Button color={ButtonColor.Primary}>Secondary</Button>
    <Button color={ButtonColor.Primary}>Success</Button>
</ButtonGroup>`;

const ButtonMixedCode = `<ButtonGroup>
    <Button color={ButtonColor.Danger}>Danger</Button>
    <Button color={ButtonColor.Warning}>Warning</Button>
    <Button color={ButtonColor.Success}>Success</Button>
</ButtonGroup>`;

const ButtonOutlinedCode = `<ButtonGroup>
    <Button color={ButtonColor.OutlinePrimary}>Left</Button>
    <Button color={ButtonColor.OutlinePrimary}>Middle</Button>
    <Button color={ButtonColor.OutlinePrimary}>Right</Button>
</ButtonGroup>`;

const ButtonLGSizeCode = `<ButonGroup size={ButtonGroupSize.Lg}>
    <Button color={ButtonColor.OutlineDark}>Left</Button>
    <Button color={ButtonColor.OutlineDark}>Middle</Button>
    <Button color={ButtonColor.OutlineDark}>Right</Button>
</ButonGroup>`;

const ButtonSMSizeCode = `<ButtonGroup size={ButtonGroupSize.Sm}>
    <Button color={ButtonColor.OutlineDark}>Left</Button>
    <Button color={ButtonColor.OutlineDark}>Middle</Button>
    <Button color={ButtonColor.OutlineDark}>Right</Button>
</ButtonGroup>`;

const ButtonToolbarCode = `<Stack gap={Gap.Gap2}>
    <ButtonGroup>
        <Button color={ButtonColor.Primary}>1</Button>
        <Button color={ButtonColor.Primary}>2</Button>
        <Button color={ButtonColor.Primary}>3</Button>
    </ButtonGroup>
    <ButtonGroup>
        <Button color={ButtonColor.Success}>4</Button>
        <Button color={ButtonColor.Success}>5</Button>
    </ButtonGroup>
    <ButtonGroup>
        <Button color={ButtonColor.Danger}>6</Button>
    </ButtonGroup>
</Stack>`;

const ButtonVerticalCode = `<ButtonGroup vertical={true}>
    <Button color={ButtonColor.Primary}>1</Button>
    <Button color={ButtonColor.Primary}>2</Button>
    <Button color={ButtonColor.Primary}>3</Button>
</ButtonGroup>`;
